import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Users, MapPin } from 'lucide-react';
import { seriesInfo } from '../data/series';
import { getLatestBook, getUpcomingBooks } from '../data/books';
import { NewsletterSignup } from '../components/ui/NewsletterSignup';

export const HomePage: React.FC = () => {
  const latestBook = getLatestBook();
  const upcomingBooks = getUpcomingBooks();

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center text-center section-atmospheric pt-36">
        <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Series Title */}
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-serif font-bold text-atmospheric mb-4 animate-fade-in-up leading-tight">
              Memory Keeper
            </h1>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif text-fog-300 mb-6 animate-fade-in-up animate-delay-200">
              Mysteries
            </h2>
            <div className="w-32 h-1 bg-forest-500 mx-auto rounded-full animate-glow-pulse animate-delay-500"></div>
          </div>

          {/* Tagline */}
          <p className="text-lg sm:text-xl md:text-2xl text-cedar-glow font-medium mb-6 typewriter animate-fade-in-up animate-delay-700">
            "{seriesInfo.tagline}"
          </p>

          {/* Series Description */}
          <p className="text-base sm:text-lg text-fog-400 max-w-2xl mx-auto leading-relaxed mb-8 animate-fade-in-up animate-delay-1000">
            {seriesInfo.description.short}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8 animate-fade-in-up animate-delay-1000">
            <Link
              to="/books"
              className="btn btn-primary text-lg px-8 py-4 hover-lift flex items-center"
            >
              <Eye className="w-5 h-5 mr-2" />
              Explore the Series
            </Link>
            <Link
              to="/cedar-falls"
              className="btn btn-secondary text-lg px-8 py-4 hover-lift flex items-center"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Visit Cedar Falls
            </Link>
          </div>

          {/* Enter Cedar Falls */}
          <div className="glass-dark px-6 py-4 rounded-xl inline-block">
            <Link
              to="/cedar-falls"
              className="flex items-center space-x-2 text-fog-300 hover:text-forest-400 transition-colors"
            >
              <MapPin className="w-4 h-4" />
              <span>Enter Cedar Falls, Washington</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-fog-500 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-fog-500 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>


      {/* Latest Book Spotlight */}
      {latestBook && (
        <section className="section bg-cedar-pattern">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-serif font-bold text-fog-100 mb-6">
                  Latest Release
                </h2>
                <div className="card-atmospheric p-8">
                  <h3 className="text-2xl font-serif font-semibold text-forest-400 mb-2">
                    {latestBook.title}
                  </h3>
                  <p className="text-fog-500 mb-4">Book {latestBook.number}</p>
                  <p className="text-fog-400 leading-relaxed mb-6">
                    {latestBook.shortDescription}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {latestBook.themes.slice(0, 3).map((theme) => (
                      <span
                        key={theme}
                        className="px-3 py-1 bg-cedar-700/50 text-fog-300 text-sm rounded-full border border-cedar-600/30"
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/books/${latestBook.id}`}
                    className="btn btn-primary"
                  >
                    Read More <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
              <div className="relative">
                <div className="book-cover max-w-md mx-auto">
                  <img
                    src={latestBook.coverImage}
                    alt={latestBook.coverImageAlt}
                    className="w-full h-auto rounded-lg shadow-atmospheric"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Series Features */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-fog-100 mb-6">
              A Series Unlike Any Other
            </h2>
            <p className="text-xl text-fog-400 max-w-3xl mx-auto leading-relaxed">
              Explore the dangerous world of recovered memories in the atmospheric Pacific Northwest
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Psychological Authenticity */}
            <div className="card-atmospheric p-8 text-center">
              <div className="w-16 h-16 bg-forest-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Eye className="w-8 h-8 text-forest-400" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-fog-100 mb-4">
                Psychological Authenticity
              </h3>
              <p className="text-fog-400 leading-relaxed">
                Real memory recovery research integrated into compelling fiction, bringing authentic psychological depth to every case.
              </p>
            </div>

            {/* Atmospheric Setting */}
            <div className="card-atmospheric p-8 text-center">
              <div className="w-16 h-16 bg-forest-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-forest-400" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-fog-100 mb-4">
                Cedar Falls, Washington
              </h3>
              <p className="text-fog-400 leading-relaxed">
                A fog-shrouded Pacific Northwest town where everyone knows everyone's business—and some secrets are deadly.
              </p>
            </div>

            {/* Character Development */}
            <div className="card-atmospheric p-8 text-center">
              <div className="w-16 h-16 bg-forest-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-forest-400" />
              </div>
              <h3 className="text-xl font-serif font-semibold text-fog-100 mb-4">
                Complex Characters
              </h3>
              <p className="text-fog-400 leading-relaxed">
                Multi-book character development arcs that reward loyal readers with deepening relationships and growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Books Teaser */}
      {upcomingBooks.length > 0 && (
        <section className="section section-atmospheric">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <h2 className="text-4xl font-serif font-bold text-fog-100 mb-6">
              More Mysteries Await
            </h2>
            <p className="text-xl text-fog-400 mb-12 max-w-2xl mx-auto">
              The secrets of Cedar Falls run deeper than anyone imagined
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
              {upcomingBooks.slice(0, 2).map((book) => (
                <div key={book.id} className="glass-dark p-6 rounded-xl">
                  <h3 className="text-xl font-serif font-semibold text-forest-400 mb-2">
                    Book {book.number}: {book.title}
                  </h3>
                  {book.publishDate && (
                    <p className="text-amber-500 text-sm mb-4">
                      Coming {new Date(book.publishDate).getFullYear()}
                    </p>
                  )}
                  <p className="text-fog-400 text-sm leading-relaxed">
                    {book.shortDescription}
                  </p>
                </div>
              ))}
            </div>

            <Link
              to="/books"
              className="btn btn-ghost"
            >
              View All Books <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="section bg-cedar-gradient">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="card-atmospheric p-8 lg:p-12">
            <NewsletterSignup 
              variant="inline"
              title="Enter the World of Memory Keeper Mysteries"
              description="Join our newsletter for exclusive content, early access to new releases, and behind-the-scenes insights from the fog-shrouded town of Cedar Falls."
            />
          </div>
        </div>
      </section>
    </div>
  );
};